import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Trainer } from '../../../models/Trainer'
import { useUsers } from '../../../state/entities'
import useTrainerSuggestions from '../../../state/queries/useTrainerSuggestions'
import useDialog from '../../../utilities/useDialog'
import Button from '../../basics/Button/Button'
import formikConnectedMultiItemSelect from '../../basics/ItemSelect/formikConnectedMultiItemSelect'
import multiItemSelect from '../../basics/ItemSelect/multiItemSelect'
import EditTrainerForm from '../../EditTrainerForm/EditTrainerForm'
import styles from '../SelectCategories/SelectCategories.module.scss'

/**********************************************************************
 * SelectTrainers
 **********************************************************************/
const SelectTrainersCombobox = multiItemSelect({
  multiSelect: true,
  useSuggestions: () => useTrainerSuggestions(),
  renderItemDescription: (user) => `${user.firstname} ${user.lastname}`,
})

// At the moment, there is no relation between the provider of a course
// and the trainers that can be selected. That's why the provider uuid is
// unused.
const SelectTrainers = React.memo(function SelectTrainers({
  providerUuid: _,
  allowCreate,
  ...props
}: { providerUuid?: string | null | undefined; allowCreate?: boolean } & React.ComponentProps<
  typeof SelectTrainersCombobox
>) {
  const { t } = useTranslation()
  const showDialog = useDialog()

  const { setItems } = props
  const handleCreateTrainer = useCallback(async () => {
    const trainer = await showDialog(
      (close) => <EditTrainerForm onClose={close} />,
      () => undefined as Trainer | undefined
    )

    if (trainer) {
      setItems((prev) => [...prev, trainer])
    }
  }, [setItems])

  return (
    <div className={styles.SelectCategories}>
      <SelectTrainersCombobox {...props} />
      {allowCreate && (
        <Button small onClick={handleCreateTrainer} disabled={props.disabled}>
          {t('category.cta create')}
        </Button>
      )}
    </div>
  )
})

/**********************************************************************
 * SelectTrainersFormik
 **********************************************************************/
export const SelectTrainersFormik = formikConnectedMultiItemSelect({
  component: SelectTrainers,
  useItems: (uuids) => useUsers(uuids).users,
  mapItems: (users) => users.map(({ uuid }) => uuid),
})
